/* eslint-disable no-undef */
/**
 * VITE_BACKEND_UI_URL is a comma-separated allowlist of the lab origins permitted to
 * exchange auth tokens with this embedded builder.
 *
 * It has to be a list: node-engineering.<env> is a SINGLE deployment embedded by BOTH
 * the old lab (old.labs.<env>) and the new one (labs.<env>) after the old/new split. A
 * single origin meant the other lab's iframe could never complete the handshake — the
 * inbound check rejected its message and the outbound postMessage was dropped by the
 * browser for targetOrigin mismatch, so the builder just timed out.
 *
 * Entries are trimmed and stripped of any trailing slash so they compare equal to
 * event.origin, which never carries one.
 */
export function parseAllowedParentOrigins(raw: string | undefined): string[] {
    return (raw ?? "")
        .split(",")
        .map((origin) => origin.trim().replace(/\/+$/, ""))
        .filter(Boolean);
}

class TokenManager {
    // eslint-disable-next-line no-use-before-define
    private static instance: TokenManager;
    private token: string | null = null;
    private tokenExpiry: number | null = null;

    private constructor() {}

    public static getInstance(): TokenManager {
        if (!TokenManager.instance) {
            TokenManager.instance = new TokenManager();
        }
        return TokenManager.instance;
    }

    public async getToken(): Promise<string> {
        // Check if the token exists and is still valid
        if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry - 5000) {
            return this.token;
        }

        // If the token is expired or doesn't exist, request a new one
        this.token = await this.requestNewToken();
        return this.token;
    }

    private async requestNewToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            const allowedOrigins = parseAllowedParentOrigins(import.meta.env.VITE_BACKEND_UI_URL);

            const receiveMessage = (event: MessageEvent) => {
                if (!allowedOrigins.includes(event.origin)) {
                    return;
                }

                if (event.data && event.data.token && event.data.expiry) {
                    window.removeEventListener("message", receiveMessage);

                    this.token = event.data.token;
                    this.tokenExpiry = event.data.expiry;
                    resolve(this.token!);
                }
            };

            window.addEventListener("message", receiveMessage);

            // postMessage takes ONE targetOrigin, so ask each allowed parent in turn.
            // The browser delivers only to the frame whose origin actually matches and
            // silently drops the rest, so this never leaks the request to a third party.
            for (const origin of allowedOrigins) {
                window.parent.postMessage("requestToken", origin);
            }

            setTimeout(() => {
                window.removeEventListener("message", receiveMessage);
                reject(new Error("Token request timed out"));
            }, 5000);
        });
    }
}

export default TokenManager;
